/**
 * Split text into plain runs and @mentions, so a mention can wear the chip —
 * in the layer behind the composer's textarea, and in the question bubble the
 * message lands in once it is sent.
 *
 * An "@" only opens a mention at a word boundary: `priya@example.com` is an
 * address and `a@b` is a typo, neither is a tag. The text itself is untouched —
 * the message that gets sent is still the plain string the user typed.
 */
import PropTypes from 'prop-types';

const MENTION_RE = /(^|\s)(@\w+)/g;

/** @returns {{text: string, isMention: boolean}[]} runs, in order. */
export const splitMentions = (text = '') => {
  const parts = [];
  let last = 0;
  let match;
  MENTION_RE.lastIndex = 0;
  while ((match = MENTION_RE.exec(text)) !== null) {
    const start = match.index + match[1].length;
    if (start > last) parts.push({ text: text.slice(last, start), isMention: false });
    parts.push({ text: match[2], isMention: true });
    last = start + match[2].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last), isMention: false });
  return parts;
};

/**
 * `text` with every @mention wearing the chip. Shared by the composer's
 * highlight layer and the sent question bubble, so a mention cannot look like
 * one thing while it is being typed and another once it is sent.
 *
 * In the bubble the chip could afford real padding, since nothing there has to
 * line up with a textarea underneath. It deliberately does not take any: the
 * two surfaces sit one above the other on screen and the send is the only
 * thing between them, so a chip that changed shape on the way would read as
 * the message having changed.
 */
export const MentionText = ({ text = '' }) => splitMentions(text).map((part, i) => (part.isMention
  ? <span key={i} className="mention-pill">{part.text}</span>
  : <span key={i}>{part.text}</span>));

MentionText.propTypes = { text: PropTypes.string };
